import { CAMPAIGN_ORDER } from "../gameplay/EncounterDirector";
import { DIFFICULTY_OPTIONS, DIFFICULTY_PRESETS, sanitizeDifficultyId, type DifficultyId } from "../gameplay/Difficulty";
import type { ContinuousProgress } from "../gameplay/ProgressManager";
import { GHOST_DEFINITION_BY_ID, type ImplementedGhostId } from "../ghosts/GhostDefinitions";

export const FEEDBACK_API_URL = "https://api-penangkaphantu.nagakeciks.my/api/feedback";
export const TURNSTILE_SITE_KEY = "0x4AAAAAAFvGj-pTk8EBZIVo";
export const TURNSTILE_ACTION = "feedback_submit";
export const FEEDBACK_SUCCESS_MESSAGE = "Terima kasih! Maklum balas anda berjaya dihantar.";
export const FEEDBACK_MESSAGE_MAX_LENGTH = 2000;

export const FEEDBACK_TYPES = ["bug", "suggestion", "gameplay", "other"] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

export interface FeedbackContext {
    readonly currentLevel: number;
    readonly currentGhost: ImplementedGhostId;
    readonly difficulty: DifficultyId;
    readonly gameVersion: string;
    readonly deviceInfo: string;
}

export interface FeedbackDraft {
    readonly playerName: string;
    readonly feedbackType: FeedbackType;
    readonly rating: number;
    readonly relatedGhost: ImplementedGhostId | "";
    readonly difficulty: DifficultyId;
    readonly subject: string;
    readonly message: string;
}

export interface FeedbackPayload {
    readonly player_name?: string;
    readonly feedback_type: FeedbackType;
    readonly rating: number;
    readonly related_ghost?: ImplementedGhostId;
    readonly difficulty: DifficultyId;
    readonly subject?: string;
    readonly message: string;
    readonly current_level: number;
    readonly current_ghost: ImplementedGhostId;
    readonly game_version: string;
    readonly device_info: string;
    readonly turnstileToken: string;
}

interface TurnstileRenderOptions {
    readonly sitekey: string;
    readonly action: string;
    readonly theme: "dark";
    readonly language: "ms";
    readonly size: "compact" | "flexible";
    readonly callback: (token: string) => void;
    readonly "expired-callback": () => void;
    readonly "error-callback": () => void;
}

interface TurnstileApi {
    render(container: HTMLElement, options: TurnstileRenderOptions): string;
    reset(widgetId?: string): void;
    remove(widgetId: string): void;
}

declare global {
    interface Window { turnstile?: TurnstileApi; }
}

let turnstileLoader: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoader) return turnstileLoader;
    turnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
        const finish = (): void => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile API tidak tersedia."));
        const existing = document.querySelector<HTMLScriptElement>('script[data-penangkap-hantu-turnstile="true"]');
        if (existing) {
            existing.addEventListener("load", finish, { once: true });
            existing.addEventListener("error", () => reject(new Error("Turnstile gagal dimuatkan.")), { once: true });
            return;
        }
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.penangkapHantuTurnstile = "true";
        script.addEventListener("load", finish, { once: true });
        script.addEventListener("error", () => {
            turnstileLoader = undefined;
            reject(new Error("Turnstile gagal dimuatkan."));
        }, { once: true });
        document.head.append(script);
    });
    return turnstileLoader;
}

const cleanOptional = (value: string, maximum: number): string | undefined => {
    const clean = value.trim();
    return clean ? clean.slice(0, maximum) : undefined;
};

export function validateFeedbackDraft(draft: FeedbackDraft): string | undefined {
    if (!FEEDBACK_TYPES.includes(draft.feedbackType)) return "Pilih jenis maklum balas.";
    if (!Number.isInteger(draft.rating) || draft.rating < 1 || draft.rating > 5) return "Pilih penilaian antara 1 hingga 5 bintang.";
    if (!draft.message.trim()) return "Tulis mesej maklum balas sebelum menghantar.";
    if (draft.message.trim().length > FEEDBACK_MESSAGE_MAX_LENGTH) return `Mesej tidak boleh melebihi ${FEEDBACK_MESSAGE_MAX_LENGTH} aksara.`;
    return undefined;
}

export function createFeedbackPayload(draft: FeedbackDraft, context: FeedbackContext, turnstileToken: string): FeedbackPayload {
    return {
        ...(cleanOptional(draft.playerName, 80) ? { player_name: cleanOptional(draft.playerName, 80) } : {}),
        feedback_type: draft.feedbackType,
        rating: draft.rating,
        ...(draft.relatedGhost ? { related_ghost: draft.relatedGhost } : {}),
        difficulty: sanitizeDifficultyId(draft.difficulty),
        ...(cleanOptional(draft.subject, 160) ? { subject: cleanOptional(draft.subject, 160) } : {}),
        message: draft.message.trim(),
        current_level: context.currentLevel,
        current_ghost: context.currentGhost,
        game_version: context.gameVersion,
        device_info: context.deviceInfo,
        turnstileToken,
    };
}

export function isConfirmedFeedbackSuccess(status: number, body: unknown): boolean {
    return status === 201 && typeof body === "object" && body !== null && (body as { success?: unknown }).success === true;
}

export async function postFeedback(payload: FeedbackPayload, fetcher: typeof fetch = fetch): Promise<void> {
    const response = await fetcher(FEEDBACK_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    let body: { error?: unknown; message?: unknown; success?: unknown } = {};
    try {
        body = await response.json() as typeof body;
    } catch { /* The status-specific message below remains authoritative. */ }
    if (isConfirmedFeedbackSuccess(response.status, body)) return;
    const apiMessage = typeof body.error === "string" ? body.error : typeof body.message === "string" ? body.message : "";
    if (response.status === 403 || /turnstile/i.test(apiMessage)) throw new Error("TURNSTILE_REJECTED");
    if (response.status === 429) throw new Error("Terlalu banyak cubaan. Sila tunggu sebentar dan cuba lagi.");
    if (response.status === 400) throw new Error("Maklumat yang dihantar tidak sah. Semak borang dan cuba lagi.");
    if (response.ok) throw new Error("Pelayan tidak mengesahkan penghantaran. Sila cuba lagi.");
    throw new Error("Maklum balas tidak dapat dihantar sekarang. Sila cuba lagi.");
}

function currentContext(progress: ContinuousProgress): FeedbackContext {
    const currentLevel = Math.max(1, Math.min(CAMPAIGN_ORDER.length, Math.floor(progress.campaignLevel)));
    const currentGhost = CAMPAIGN_ORDER[currentLevel - 1] ?? "toyol";
    const nav = navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } };
    const platform = nav.userAgentData?.platform || navigator.platform || "tidak diketahui";
    const mobile = nav.userAgentData?.mobile ?? matchMedia("(pointer: coarse)").matches;
    return {
        currentLevel,
        currentGhost,
        difficulty: sanitizeDifficultyId(progress.difficultyId),
        gameVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
        deviceInfo: `${platform}; ${mobile ? "sentuh/mudah alih" : "desktop"}; ${screen.width}x${screen.height}; ${navigator.language}; ${navigator.userAgent}`.slice(0, 500),
    };
}

const ghostOptionsMarkup = (selected: ImplementedGhostId): string => [
    '<option value="">Umum / Tiada</option>',
    ...CAMPAIGN_ORDER.map((id) => `<option value="${id}"${id === selected ? " selected" : ""}>${GHOST_DEFINITION_BY_ID[id].displayName}</option>`),
].join("");

const difficultyOptionsMarkup = (selected: DifficultyId): string => DIFFICULTY_OPTIONS
    .map(({ id, label }) => `<option value="${id}"${id === selected ? " selected" : ""}>${label}</option>`).join("");

const FEEDBACK_MOBILE_STYLES = `
@media (max-width: 767px) {
  .feedback-screen {
    position: fixed !important;
    inset: 0 !important;
    z-index: 1000 !important;
    width: 100% !important;
    max-width: none !important;
    height: 100dvh !important;
    min-height: 0 !important;
    display: block !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
    box-sizing: border-box;
    padding: calc(env(safe-area-inset-top, 0px) + 10px) 12px calc(env(safe-area-inset-bottom, 0px) + 20px) !important;
    background: #061710 !important;
  }
  .feedback-screen *, .feedback-screen *::before, .feedback-screen *::after { box-sizing: border-box; }
  .feedback-screen .feedback-card {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    margin: 0 auto !important;
    padding: 16px 14px 20px !important;
    overflow: visible !important;
  }
  .feedback-screen .feedback-header {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    align-items: start !important;
    gap: 8px !important;
    position: static !important;
    padding: 0 0 12px !important;
    margin: 0 0 14px !important;
  }
  .feedback-screen .feedback-header .back {
    position: static !important;
    inset: auto !important;
    transform: none !important;
    justify-self: start !important;
    width: auto !important;
    min-height: 42px !important;
    margin: 0 !important;
    padding: 7px 12px !important;
    font-size: 15px !important;
    line-height: 1.2 !important;
  }
  .feedback-screen .feedback-header > div { min-width: 0; }
  .feedback-screen .feedback-header .eyebrow { margin: 0 0 2px !important; font-size: 11px !important; letter-spacing: .18em; }
  .feedback-screen .feedback-header h2 {
    margin: 0 !important;
    font-size: clamp(25px, 7vw, 32px) !important;
    line-height: 1.1 !important;
    letter-spacing: .02em !important;
    overflow-wrap: anywhere;
  }
  .feedback-screen .feedback-header > p {
    grid-column: 1 !important;
    margin: 0 !important;
    font-size: 13px !important;
    line-height: 1.4 !important;
  }
  .feedback-screen .feedback-form, .feedback-screen .feedback-grid {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    width: 100% !important;
    min-width: 0 !important;
    gap: 12px !important;
  }
  .feedback-screen .feedback-grid > *, .feedback-screen .feedback-form > * {
    min-width: 0 !important;
    width: 100%;
    grid-column: 1 / -1 !important;
  }
  .feedback-screen .feedback-grid > label {
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
    margin: 0 !important;
    font-size: 14px !important;
  }
  .feedback-screen .feedback-grid input:not([type=radio]),
  .feedback-screen .feedback-grid select,
  .feedback-screen .feedback-grid textarea {
    display: block;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    min-height: 44px !important;
    margin: 0 !important;
    padding: 10px 12px !important;
    font-size: 16px !important; /* prevents Safari focus zoom */
  }
  .feedback-screen .feedback-grid textarea {
    height: 125px !important;
    min-height: 110px !important;
    max-height: 220px !important;
    resize: vertical;
  }
  .feedback-screen .feedback-rating {
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
  }
  .feedback-screen .feedback-rating legend { margin: 0 0 6px !important; padding: 0 !important; font-size: 14px !important; }
  .feedback-screen .rating-options {
    display: grid !important;
    grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
    gap: 7px !important;
    width: 100% !important;
  }
  .feedback-screen .rating-options label {
    width: 100% !important;
    min-width: 0 !important;
    min-height: 44px !important;
    height: 48px !important;
    padding: 4px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .feedback-screen .rating-options label [aria-hidden=true] { font-size: 26px !important; line-height: 1 !important; }
  .feedback-screen .message-count { align-self: end; margin: 0 !important; }
  .feedback-screen .feedback-context {
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 7px !important;
    padding: 12px !important;
    min-height: 0 !important;
    margin: 0 !important;
  }
  .feedback-screen .feedback-context > strong { width: 100% !important; margin: 0 !important; font-size: 13px !important; }
  .feedback-screen .feedback-context > small { width: 100% !important; margin: 0 !important; }
  .feedback-screen .turnstile-wrap { min-width: 0; max-width: 100%; overflow: hidden; }
  .feedback-screen .turnstile-container { max-width: 100%; }
  .feedback-screen .feedback-submit { width: 100% !important; min-height: 48px !important; margin: 0 !important; }
  .feedback-screen .feedback-success-backdrop {
    position: fixed !important;
    inset: 0 !important;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px);
  }
  .feedback-screen .feedback-success-dialog { width: min(100%, 380px); max-height: 90dvh; overflow-y: auto; }
}
`;


function installMobileStyles(id: string, css: string): void {
    let element = document.getElementById(id) as HTMLStyleElement | null;
    if (!element) {
        element = document.createElement("style");
        element.id = id;
        document.head.appendChild(element);
    }
    if (element.textContent !== css) element.textContent = css;
}

export class FeedbackForm {
    private turnstile?: TurnstileApi;
    private widgetId?: string;
    private token = "";
    private submitting = false;
    private submitted = false;
    private disposed = false;

    constructor(
        private readonly root: HTMLElement,
        private readonly progress: ContinuousProgress,
        private readonly onBack: () => void,
    ) { }

    show(): void {
        installMobileStyles("ph-feedback-mobile-fix-v2", FEEDBACK_MOBILE_STYLES);
        if (import.meta.env.DEV) console.info("[UI] Feedback mobile fix v2 active", matchMedia("(max-width: 767px)").matches);
        const context = currentContext(this.progress);
        const currentGhostName = GHOST_DEFINITION_BY_ID[context.currentGhost].displayName;
        this.root.innerHTML = `<main class="screen sub-screen feedback-screen"><section class="wide-card glass-panel feedback-card">
      <header class="feedback-header"><button class="back" type="button">← Kembali</button><div><p class="eyebrow">SUARA PEMAIN</p><h2>MAKLUM BALAS</h2></div><p>Bantu Din Belut memperbaiki pemburuan seterusnya.</p></header>
      <form class="feedback-form" novalidate>
        <div class="feedback-grid">
          <label><span>Nama pemain <small>(pilihan)</small></span><input name="playerName" type="text" maxlength="80" autocomplete="name" placeholder="Nama atau nama samaran"></label>
          <label><span>Jenis maklum balas</span><select name="feedbackType" required><option value="bug">Masalah / Pepijat</option><option value="suggestion">Cadangan</option><option value="gameplay">Gameplay</option><option value="other">Lain-lain</option></select></label>
          <fieldset class="feedback-rating"><legend>Penilaian</legend><div class="rating-options">${[1, 2, 3, 4, 5].map((rating) => `<input id="feedback-rating-${rating}" name="rating" type="radio" value="${rating}"${rating === 5 ? " checked" : ""}><label for="feedback-rating-${rating}" title="${rating} bintang"><span aria-hidden="true">★</span><span class="sr-only">${rating} bintang</span></label>`).join("")}</div></fieldset>
          <label><span>Hantu berkaitan</span><select name="relatedGhost">${ghostOptionsMarkup(context.currentGhost)}</select></label>
          <label><span>Tahap kesukaran semasa</span><select name="difficulty">${difficultyOptionsMarkup(context.difficulty)}</select></label>
          <label><span>Subjek <small>(pilihan)</small></span><input name="subject" type="text" maxlength="160" placeholder="Ringkasan maklum balas"></label>
          <label class="feedback-message"><span>Mesej maklum balas</span><textarea name="message" required maxlength="${FEEDBACK_MESSAGE_MAX_LENGTH}" rows="5" placeholder="Ceritakan pengalaman, masalah atau cadangan anda..."></textarea><small class="message-count">0 / ${FEEDBACK_MESSAGE_MAX_LENGTH}</small></label>
        </div>
        <aside class="feedback-context" aria-label="Konteks permainan yang disertakan"><strong>Konteks automatik</strong><span>Level ${context.currentLevel}</span><span>${currentGhostName}</span><span>${DIFFICULTY_PRESETS[context.difficulty].label}</span><span>v${context.gameVersion}</span><small>Maklumat peranti asas turut disertakan untuk membantu siasatan.</small></aside>
        <div class="turnstile-wrap"><div class="turnstile-container" aria-label="Pengesahan keselamatan"></div><small class="turnstile-help">Pengesahan keselamatan diperlukan sebelum menghantar.</small></div>
        <p class="feedback-status" role="status" aria-live="polite"></p>
        <button class="primary feedback-submit" type="submit" disabled>HANTAR MAKLUM BALAS</button>
      </form>
    </section></main>`;
        const form = this.root.querySelector<HTMLFormElement>(".feedback-form");
        const message = this.root.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
        message?.addEventListener("input", () => {
            const counter = this.root.querySelector<HTMLElement>(".message-count");
            if (counter) counter.textContent = `${message.value.length} / ${FEEDBACK_MESSAGE_MAX_LENGTH}`;
        });
        this.root.querySelector(".back")?.addEventListener("click", () => { this.dispose(); this.onBack(); });
        form?.addEventListener("submit", (event) => { event.preventDefault(); void this.submit(form, context); });
        void this.renderTurnstile();
    }

    dispose(): void {
        this.disposed = true;
        if (this.turnstile && this.widgetId) this.turnstile.remove(this.widgetId);
        this.widgetId = undefined;
        this.turnstile = undefined;
        this.token = "";
    }

    private async renderTurnstile(): Promise<void> {
        try {
            const api = await loadTurnstile();
            if (this.disposed) return;
            const container = this.root.querySelector<HTMLElement>(".turnstile-container");
            if (!container) return;
            this.turnstile = api;
            this.widgetId = api.render(container, {
                sitekey: TURNSTILE_SITE_KEY,
                action: TURNSTILE_ACTION,
                theme: "dark",
                language: "ms",
                size: matchMedia("(max-width: 380px)").matches ? "compact" : "flexible",
                callback: (token) => {
                    this.token = token;
                    this.setSubmitEnabled(true);
                    if (!this.submitting && !this.submitted) this.setStatus("Pengesahan selesai. Borang sedia dihantar.", "ready");
                },
                "expired-callback": () => {
                    this.token = "";
                    this.setSubmitEnabled(false);
                    this.setStatus("Pengesahan keselamatan tamat tempoh. Sila sahkan semula.", "error");
                },
                "error-callback": () => {
                    this.token = "";
                    this.setSubmitEnabled(false);
                    this.setStatus("Pengesahan keselamatan gagal. Semak sambungan dan cuba lagi.", "error");
                },
            });
        } catch {
            if (!this.disposed) this.setStatus("Pengesahan keselamatan tidak dapat dimuatkan. Semak sambungan dan cuba lagi.", "error");
        }
    }

    private async submit(form: HTMLFormElement, context: FeedbackContext): Promise<void> {
        if (this.submitting || this.submitted) return;
        const data = new FormData(form);
        const draft: FeedbackDraft = {
            playerName: String(data.get("playerName") ?? ""),
            feedbackType: String(data.get("feedbackType") ?? "") as FeedbackType,
            rating: Number(data.get("rating")),
            relatedGhost: String(data.get("relatedGhost") ?? "") as ImplementedGhostId | "",
            difficulty: sanitizeDifficultyId(data.get("difficulty")),
            subject: String(data.get("subject") ?? ""),
            message: String(data.get("message") ?? ""),
        };
        const validationError = validateFeedbackDraft(draft);
        if (validationError) {
            this.setStatus(validationError, "error");
            form.reportValidity();
            return;
        }
        if (!this.token) {
            this.setStatus("Lengkapkan pengesahan keselamatan sebelum menghantar.", "error");
            return;
        }
        this.submitting = true;
        this.setSubmitEnabled(false);
        this.setStatus("Maklum balas sedang dihantar…", "loading");
        try {
            await postFeedback(createFeedbackPayload(draft, context, this.token));
            this.submitted = true;
            this.submitting = false;
            this.setStatus(FEEDBACK_SUCCESS_MESSAGE, "success");
            this.showSuccessDialog(form);
        } catch (error) {
            const turnstileRejected = error instanceof Error && error.message === "TURNSTILE_REJECTED";
            this.token = "";
            if (this.turnstile && this.widgetId) this.turnstile.reset(this.widgetId);
            this.setStatus(turnstileRejected ? "Pengesahan keselamatan tidak sah atau tamat tempoh. Sila sahkan semula." : error instanceof Error ? error.message : "Maklum balas tidak dapat dihantar sekarang. Sila cuba lagi.", "error");
        } finally {
            this.submitting = false;
            if (!this.submitted && this.token) this.setSubmitEnabled(true);
        }
    }

    private showSuccessDialog(form: HTMLFormElement): void {
        const screen = this.root.querySelector<HTMLElement>(".feedback-screen");
        const card = this.root.querySelector<HTMLElement>(".feedback-card");
        if (!screen || !card) return;
        card.inert = true;
        screen.insertAdjacentHTML("beforeend", `<div class="feedback-success-backdrop"><section class="feedback-success-dialog glass-panel" role="alertdialog" aria-modal="true" aria-labelledby="feedback-success-title" aria-describedby="feedback-success-message"><p class="eyebrow">MAKLUM BALAS DITERIMA</p><h2 id="feedback-success-title">BERJAYA DIHANTAR</h2><p id="feedback-success-message">${FEEDBACK_SUCCESS_MESSAGE}</p><button class="primary feedback-success-ok" type="button">OK</button></section></div>`);
        const ok = this.root.querySelector<HTMLButtonElement>(".feedback-success-ok");
        ok?.addEventListener("click", () => {
            this.root.querySelector(".feedback-success-backdrop")?.remove();
            card.inert = false;
            form.reset();
            const counter = this.root.querySelector<HTMLElement>(".message-count");
            if (counter) counter.textContent = `0 / ${FEEDBACK_MESSAGE_MAX_LENGTH}`;
            this.dispose();
            this.onBack();
        }, { once: true });
        ok?.focus();
    }

    private setSubmitEnabled(enabled: boolean): void {
        const button = this.root.querySelector<HTMLButtonElement>(".feedback-submit");
        if (button) button.disabled = !enabled || this.submitting || this.submitted;
    }

    private setStatus(message: string, state: "ready" | "loading" | "error" | "success"): void {
        const status = this.root.querySelector<HTMLElement>(".feedback-status");
        if (!status) return;
        status.textContent = message;
        status.dataset.state = state;
    }
}
